import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useCreateExpenseMutation,
  useUpdateExpenseMutation,
  useRemoveExpenseMutation,
  useSetExpenseStatusMutation,
  ExpenseDocument,
  ExpenseInput,
  UpdateExpenseInput,
} from 'core/graphql';

import { useToasts } from '../Toast/useToasts';
import { evictExpenseLists } from './evictFinanceLists';

interface IExpenseMutationsHook {
  createExpense: (input: ExpenseInput) => Promise<boolean>;
  updateExpense: (input: UpdateExpenseInput) => Promise<boolean>;
  /** One-click status change; backend derives `paidAt` from the status. */
  setStatus: (
    id: string,
    status: string,
    paidAt?: string | null,
  ) => Promise<boolean>;
  deleteExpense: (id: string) => Promise<boolean>;
  loading: boolean;
}

/**
 * One hook for CRUD on expenses (TZ §3.6–§3.7). The backend silently
 * ignores locked fields on Fakturownia-sourced rows and returns a warning
 * inside `errors[]` — we surface it as a non-blocking toast so the user
 * sees what stuck and what didn't (`success` stays `true`).
 */
export const useExpenseMutations = (): IExpenseMutationsHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  // Finance-block audit: the old no-variables `[{ query: ExpensesDocument }]`
  // refetch was dead weight — Apollo ran an extra UNFILTERED company-wide
  // expenses query per mutation and wrote it under the argless cache key no
  // consumer reads. Evicting the drained field instead invalidates every
  // cached (filters, page) combo — GroupDetail Finances / TeacherDetail
  // Payouts / other date presets stop serving ghost or missing rows — while
  // the visible register converges through its own drain refetch() callback.
  const [createMutation, createState] = useCreateExpenseMutation({
    update: evictExpenseLists,
  });
  const [updateMutation, updateState] = useUpdateExpenseMutation({
    update: evictExpenseLists,
  });
  const [deleteMutation, deleteState] = useRemoveExpenseMutation({
    update: evictExpenseLists,
  });
  // The returned `expense { id status paidAt }` also patches the cached row.
  const [setStatusMutation, setStatusState] = useSetExpenseStatusMutation({
    update: evictExpenseLists,
  });

  const notify = useCallback(
    (type: ToastTypes, title: string, text?: string) => {
      addToast({
        data: { type, title, text },
        type: GlobalToastTypeNames.Expense,
      });
    },
    [addToast],
  );

  const surfaceWarnings = useCallback(
    (errors?: (string | null)[] | null) => {
      const text = errors?.filter(Boolean).join('\n');
      if (!text) return;
      notify(
        ToastTypes.warning,
        t('pages.fakturownia.toasts.ignoredFields'),
        text,
      );
    },
    [notify, t],
  );

  const createExpense = useCallback(
    async (input: ExpenseInput) => {
      try {
        const result = await createMutation({ variables: { input } });
        const payload = result.data?.createExpense;
        if (payload?.success) {
          notify(
            ToastTypes.success,
            t('pages.fakturownia.toasts.expenseCreated'),
          );
          surfaceWarnings(payload.errors);
          return true;
        }
        notify(
          ToastTypes.error,
          t('pages.fakturownia.toasts.expenseCreateFailed'),
          payload?.errors?.filter(Boolean).join('\n') || undefined,
        );
        return false;
      } catch (error) {
        notify(
          ToastTypes.error,
          t('pages.fakturownia.toasts.expenseCreateFailed'),
          (error as Error).message,
        );
        return false;
      }
    },
    [createMutation, notify, surfaceWarnings, t],
  );

  const updateExpense = useCallback(
    async (input: UpdateExpenseInput) => {
      try {
        const result = await updateMutation({
          variables: { input },
          refetchQueries: [
            { query: ExpenseDocument, variables: { id: input.id } },
          ],
        });
        const payload = result.data?.updateExpense;
        if (payload?.success) {
          notify(
            ToastTypes.success,
            t('pages.fakturownia.toasts.expenseUpdated'),
          );
          surfaceWarnings(payload.errors);
          return true;
        }
        notify(
          ToastTypes.error,
          t('pages.fakturownia.toasts.expenseUpdateFailed'),
          payload?.errors?.filter(Boolean).join('\n') || undefined,
        );
        return false;
      } catch (error) {
        notify(
          ToastTypes.error,
          t('pages.fakturownia.toasts.expenseUpdateFailed'),
          (error as Error).message,
        );
        return false;
      }
    },
    [updateMutation, notify, surfaceWarnings, t],
  );

  const setStatus = useCallback(
    async (id: string, status: string, paidAt?: string | null) => {
      try {
        const result = await setStatusMutation({
          variables: { id, status, paidAt: paidAt ?? null },
        });
        const payload = result.data?.setExpenseStatus;
        if (payload?.success) {
          notify(
            ToastTypes.success,
            t('pages.fakturownia.toasts.statusUpdated'),
          );
          return true;
        }
        notify(
          ToastTypes.error,
          t('pages.fakturownia.toasts.statusUpdateFailed'),
          payload?.errors?.filter(Boolean).join('\n') || undefined,
        );
        return false;
      } catch (error) {
        notify(
          ToastTypes.error,
          t('pages.fakturownia.toasts.statusUpdateFailed'),
          (error as Error).message,
        );
        return false;
      }
    },
    [setStatusMutation, notify, t],
  );

  const deleteExpense = useCallback(
    async (id: string) => {
      try {
        const result = await deleteMutation({ variables: { id } });
        const payload = result.data?.deleteExpense;
        if (payload?.success) {
          notify(
            ToastTypes.success,
            t('pages.fakturownia.toasts.expenseDeleted'),
          );
          return true;
        }
        notify(
          ToastTypes.error,
          t('pages.fakturownia.toasts.expenseDeleteFailed'),
          payload?.errors?.filter(Boolean).join('\n') || undefined,
        );
        return false;
      } catch (error) {
        notify(
          ToastTypes.error,
          t('pages.fakturownia.toasts.expenseDeleteFailed'),
          (error as Error).message,
        );
        return false;
      }
    },
    [deleteMutation, notify, t],
  );

  return {
    createExpense,
    updateExpense,
    setStatus,
    deleteExpense,
    loading:
      createState.loading ||
      updateState.loading ||
      setStatusState.loading ||
      deleteState.loading,
  };
};
